/**
 * File: JudgmentPredictionDashboard.jsx
 * Path: /src/components/solutions/JudgmentPredictionDashboard.jsx
 * Description: Standalone dashboard visualization for the Judgment Prediction AI solution
 * Key Features: Interactive prediction visualization with detailed case outcome metrics
 */

import React from 'react';
import { motion } from 'framer-motion';

const JudgmentPredictionDashboard = ({
  // Default values that can be overridden with props
  outcomeData = {
    dismissal: { complete: 12, partial: 28 },
    settlement: 47,
    favorable: 13
  },
  settlementRange = {
    min: 180000,
    max: 350000,
    optimal: { min: 275000, max: 310000 },
    position: { start: 58, end: 85 } // percentages for the highlighted range
  },
  caseInfo = {
    count: 328,
    jurisdiction: 'Northern District'
  },
  metrics = {
    timeline: '14-18 months',
    judgeTendency: 'Pro-defendant',
    confidenceScore: { value: 84, rating: 'High' }
  }
}) => {
  // Format currency values
  const formatCurrency = (value) => {
    return '$' + (value / 1000).toFixed(0) + 'K';
  };

  return (
    <div className="relative rounded-xl overflow-hidden shadow-xl w-full">
      <div className="bg-gradient-to-br from-indigo-700 via-purple-700 to-blue-700 p-6">
        {/* Circuit pattern background */}
        <div className="absolute inset-0 opacity-20">
          <svg width="100%" height="100%" viewBox="0 0 800 600" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M769.5,591.5 L417.5,591.5 L417.5,196.5 L365.5,196.5 L365.5,591.5 L57.5,591.5" stroke="rgba(255,255,255,0.3)" />
              <path d="M627.5,358.5 L627.5,520.5 L493.5,520.5 L493.5,421.5 L549.5,421.5" stroke="rgba(255,255,255,0.4)" />
              <path d="M595.5,308.5 L696.5,308.5 L696.5,224.5 L492.5,224.5 L492.5,87.5 L696.5,87.5" stroke="rgba(255,255,255,0.25)" />
              <path d="M44.5,358.5 L44.5,144.5 L200.5,144.5 L200.5,242.5 L154.5,242.5" stroke="rgba(255,255,255,0.3)" />
            </g>
          </svg>
        </div>
        
        {/* Dashboard container with min-height to prevent cutting off */}
        <div className="min-h-[440px] w-full flex items-center justify-center relative z-10">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 p-6 w-full text-white">
            {/* Interface Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center mr-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
                <h3 className="font-bold text-xl">Veritect Judgment Prediction AI</h3>
              </div>
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            
            {/* Main Dashboard Content */}
            <div className="grid grid-cols-5 gap-4 mb-4">
              {/* Case Outcome Probability Section */}
              <div className="col-span-3 bg-white/5 backdrop-blur-sm rounded-lg p-3">
                <div className="text-sm font-medium mb-2">Case Outcome Probability</div>
                <div className="space-y-3">
                  {/* Complete Dismissal */}
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Complete Dismissal</span>
                      <span>{outcomeData.dismissal.complete}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-red-500 rounded-full" 
                        style={{ width: `${outcomeData.dismissal.complete}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Partial Dismissal */}
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Partial Dismissal</span>
                      <span>{outcomeData.dismissal.partial}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-orange-500 rounded-full" 
                        style={{ width: `${outcomeData.dismissal.partial}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Settlement */}
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Settlement</span>
                      <span className="font-medium">{outcomeData.settlement}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500 rounded-full" 
                        style={{ width: `${outcomeData.settlement}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Favorable Judgment */}
                  <div>
                    <div className="flex justify-between text-xs mb-1">
                      <span>Favorable Judgment</span>
                      <span>{outcomeData.favorable}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-green-500 rounded-full" 
                        style={{ width: `${outcomeData.favorable}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Settlement Range Prediction Section */}
              <div className="col-span-2 bg-white/5 backdrop-blur-sm rounded-lg p-3">
                <div className="text-sm font-medium mb-2">Settlement Range Prediction</div>
                <div className="flex items-center justify-center h-20">
                  <div className="relative w-full h-6">
                    {/* Full range gradient background */}
                    <div className="absolute inset-0 bg-gradient-to-r from-red-500 via-yellow-500 to-green-500 rounded-full opacity-30"></div>
                    
                    {/* Min and max values */}
                    <div className="absolute top-full mt-1 left-0 text-xs text-white/70">
                      {formatCurrency(settlementRange.min)}
                    </div>
                    <div className="absolute top-full mt-1 right-0 text-xs text-white/70">
                      {formatCurrency(settlementRange.max)}
                    </div>
                    
                    {/* Optimal range label */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-8 bg-white/10 backdrop-blur-sm rounded px-2 py-1 text-xs">
                      Optimal: {formatCurrency(settlementRange.optimal.min)} - {formatCurrency(settlementRange.optimal.max)}
                    </div>
                    
                    {/* Highlighted optimal range */}
                    <div 
                      className="absolute inset-y-0 bg-yellow-500/50 rounded-full border border-white/20"
                      style={{ 
                        left: `${settlementRange.position.start}%`, 
                        right: `${100 - settlementRange.position.end}%` 
                      }}
                    ></div>
                    
                    {/* Range markers */}
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-white"
                      style={{ left: `${settlementRange.position.start}%` }}
                    ></div>
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-white"
                      style={{ left: `${settlementRange.position.end}%` }}
                    ></div>
                  </div>
                </div>
                <div className="text-center text-xs text-white/70 mt-8">
                  Based on {caseInfo.count} similar cases in {caseInfo.jurisdiction}
                </div>
              </div>
            </div>
            
            {/* Bottom Metrics Section */}
            <div className="grid grid-cols-3 gap-3 mt-6">
              {/* Timeline */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/70 mb-1">Estimated Timeline</div>
                <div className="text-sm">{metrics.timeline}</div>
              </div>
              
              {/* Judge Tendency */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/70 mb-1">Judge Tendency</div>
                <div className="text-sm">{metrics.judgeTendency}</div>
              </div>
              
              {/* Confidence Score */}
              <div className="bg-white/5 rounded-lg p-2">
                <div className="text-xs text-white/70 mb-1">Confidence Score</div>
                <div className="text-sm">{metrics.confidenceScore.rating} ({metrics.confidenceScore.value}%)</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JudgmentPredictionDashboard;